import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Component, OnInit, ViewChild, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputComponent } from '@components/input';
import { TextareaComponent } from '@components/textarea';
import { CheckboxComponent } from '@components/checkbox';
import { MsfSelect } from '@components/select/select';
import { ScheduleInterval, ParametrsJsonSchemaFieldsType } from '@shared/models/background-task.models';
import { POSSIBLE_EXECUTE_TYPES } from '@shared/data/task-types.data';
import { MsfMultiSelect } from '@components/multi-select/multi-select';
import { TaskSchedulerService } from '@services/task-scheduler.service';
import {
  LocalizationString,
  PosibleExecuteType,
  SystemBackgroundTask,
  ScheduleTaskOptions,
  ScheduleType,
} from '@6grain/masofa-monolith-api-client/common';
import { SystemBackgroundTaskService } from '@6grain/masofa-monolith-api-client/common/api/systemBackgroundTask.service';
import {TASK_ID} from '@shared/data/task-type-data';

@Component({
  selector: 'app-add-task',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    ButtonModule,
    InputComponent,
    SelectModule,
    MultiSelectModule,
    TextareaComponent,
    CheckboxComponent,
    MsfSelect,
    MsfMultiSelect,
  ],
  templateUrl: './add-task.html',
  styleUrl: './add-task.scss',
})
export class AddTask implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @ViewChild('taskTypeSelect') taskTypeSelect!: MsfSelect;

  taskForm!: FormGroup;
  loading = false;

  isEditMode = false;
  editedTask: any = null;

  possibleExecuteTypes: PosibleExecuteType[] = POSSIBLE_EXECUTE_TYPES;
  selectedExecuteType: PosibleExecuteType | null = null;
  selectedTaskTypeId: TASK_ID | null = null;

  TaskTypeUses: { id?: any; names?: LocalizationString; taskType?: number }[] = [];

  months = [
    { label: 'task.months.january', value: 1 },
    { label: 'task.months.february', value: 2 },
    { label: 'task.months.march', value: 3 },
    { label: 'task.months.april', value: 4 },
    { label: 'task.months.may', value: 5 },
    { label: 'task.months.june', value: 6 },
    { label: 'task.months.july', value: 7 },
    { label: 'task.months.august', value: 8 },
    { label: 'task.months.september', value: 9 },
    { label: 'task.months.october', value: 10 },
    { label: 'task.months.november', value: 11 },
    { label: 'task.months.december', value: 12 },
  ];

  daysOfMonth = Array.from({ length: 31 }, (_, i) => ({
    label: (i + 1).toString(),
    value: i + 1,
  }));

  ScheduleType = ScheduleType;
  ScheduleInterval = ScheduleInterval;
  ParametrsJsonSchemaFieldsType = ParametrsJsonSchemaFieldsType;
  filterKeywords: string[] = [];

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private translate: TranslateService,
    private taskSchedulerService: TaskSchedulerService,
    private systemBackgroundTaskService: SystemBackgroundTaskService,
  ) {
    console.log('AddTask config data:', this.config?.data);
    this.initForm();
  }

  ngOnInit(): void {
    if (this.config?.data?.filterKeywords) {
      this.filterKeywords = this.config.data.filterKeywords;
    }
    this.loadPossibleExecuteTypes();
    if (this.config?.data?.task) {
      this.isEditMode = true;
      this.editedTask = this.config.data.task;
      this.selectedTaskTypeId = this.config?.data?.possibleExecuteType?.id;
      this.selectedExecuteType = this.config?.data?.possibleExecuteType;
    }
  }

  private loadPossibleExecuteTypes(): void {
    this.loading = true;

    this.taskSchedulerService.getSystemBackgroundTasks().pipe(takeUntil(this.destroy$)).subscribe({
      next: (types) => {
        this.possibleExecuteTypes = types;
        if (this.filterKeywords && this.filterKeywords.length > 0) {
          this.possibleExecuteTypes = types.filter((type) => {
            const typeName = type.executeTypeName || '';
            return this.filterKeywords.some((keyword) => typeName.includes(keyword));
          });
        } else {
          this.possibleExecuteTypes = types;
        }

        this.TaskTypeUses = this.possibleExecuteTypes.map((type) => {
          const shortName = type.executeTypeName?.split('.').pop() || type.executeTypeName || '';
          const displayNames =
            type.names && Object.keys(type.names).length > 0
              ? type.names
              : {
                  'en-US': shortName || '',
                  'ru-RU': shortName || '',
                  'ar-LB': shortName || '',
                  'uz-Latn-UZ': shortName || '',
                };

          return {
            id: type.executeTypeName,
            taskType: type.taskType as TASK_ID,
            names: displayNames as { 'en-US': string; 'ru-RU': string; 'ar-LB': string; 'uz-Latn-UZ': string },
          };
        });

        this.loading = false;
        this.cdr.markForCheck();

        if (this.isEditMode && this.editedTask) {
          this.loadTaskData();
        } else {
          const scheduleTask = this.possibleExecuteTypes.find((t) => t.taskType === TASK_ID.Schedule);
          if (scheduleTask) {
            this.taskForm.patchValue({
              ExecuteTypeName: scheduleTask.executeTypeName,
            });
            this.onExecuteTypeChange();
          }
        }
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private loadTaskData(): void {
    if (!this.editedTask) return;

    const data = this.possibleExecuteTypes.find((type) => type.executeTypeName === this.editedTask.id) || null;

    if (data) {
      this.selectedExecuteType = data;
    }

    let taskOptions: any = {};
    let parameters: any = {};

    try {
      if (this.editedTask.taskOptionJson) {
        taskOptions =
          typeof this.editedTask.taskOptionJson === 'string'
            ? JSON.parse(this.editedTask.taskOptionJson)
            : this.editedTask.taskOptionJson;
      }
    } catch (e) {
      console.error('Error parsing taskOptionJson:', e);
    }

    try {
      if (this.editedTask.parametrsJson) {
        parameters =
          typeof this.editedTask.parametrsJson === 'string'
            ? JSON.parse(this.editedTask.parametrsJson)
            : this.editedTask.parametrsJson;
      }
    } catch (e) {
      console.error('Error parsing parametrsJson:', e);
    }

    this.onExecuteTypeChange();

    this.taskForm.patchValue({
      ExecuteTypeName: this.editedTask.executeTypeName,
      IsActive: this.editedTask.isActive ?? true,
      MaxExecutions: this.editedTask.maxExecutions ?? -1,
      IsRetryable: this.editedTask.isRetryable ?? false,
      MaxRetryCount: this.editedTask.maxRetryCount ?? -1,
    });

    if (this.isScheduleTask() && taskOptions) {
      this.taskForm.patchValue({
        ScheduleType: taskOptions.ScheduleType ?? ScheduleType.NUMBER_1, //Daily
        ScheduleInterval: taskOptions.ScheduleInterval ?? ScheduleInterval.Hours,
        IntervalValue: taskOptions.IntervalValue ?? 1,
        LaunchTime: taskOptions.LaunchTime ?? '02:00',
        DaysOfWeek: taskOptions.DaysOfWeek ?? [],
        DaysOfMonth: taskOptions.DaysOfMonth ?? [],
        Months: taskOptions.Months ?? [],
        TimeZone: taskOptions.TimeZone ?? 'UTC',
        StartDelaySeconds: taskOptions.StartDelaySeconds ?? 0,
      });
    }

    if (this.selectedExecuteType?.parametrsJsonSchema && parameters) {
      this.selectedExecuteType.parametrsJsonSchema.forEach((field) => {
        if (field?.fieldName && parameters[field.fieldName] !== undefined) {
          this.taskForm.patchValue({
            [field.fieldName]: parameters[field.fieldName],
          });
        }
      });
    }

    this.cdr.detectChanges();
  }

  private initForm(): void {
    this.taskForm = this.fb.group({
      ExecuteTypeName: ['', Validators.required],
      IsActive: [true],
      MaxExecutions: [-1, [Validators.min(-1)]],
      IsRetryable: [false],
      MaxRetryCount: [-1, [Validators.min(-1)]],

      ScheduleType: [ScheduleType.NUMBER_1],
      ScheduleInterval: [ScheduleInterval.Hours],
      IntervalValue: [1, [Validators.min(1)]],
      LaunchTime: ['02:00'],
      DaysOfWeek: [[]],
      DaysOfMonth: [[]],
      Months: [[]],
      TimeZone: ['UTC'],
      StartDelaySeconds: [0, [Validators.min(0), Validators.max(86400000)]],
    });
  }

  onExecuteTypeChange(): void {
    if (!this.selectedExecuteType) return;

    this.removeParameterControls();

    this.selectedExecuteType?.parametrsJsonSchema?.forEach((field) => {
      if (field?.fieldName != null) {
        this.taskForm.addControl(field?.fieldName, this.fb.control(field.defaultValue || ''));
      }
    });

    this.taskForm.patchValue({
      GroupName: this.selectedExecuteType.groupName,
    });
  }

  onExecuteTypeSelectionChange(value: any): void {
    const newTaskTypeId = value;

    if (this.selectedTaskTypeId === newTaskTypeId) {
      return;
    }

    this.selectedTaskTypeId = newTaskTypeId;

    const data = this.possibleExecuteTypes.find((type) => type.executeTypeName === value) || null;
    if (data) {
      this.selectedExecuteType = data;
    }

    if (this.selectedExecuteType) {
      this.taskForm.patchValue({
        ExecuteTypeName: this.selectedExecuteType.executeTypeName,
      });
      this.onExecuteTypeChange();
    } else {
      this.removeParameterControls();
    }

    this.cdr.detectChanges();

    setTimeout(() => {
      if (this.taskTypeSelect) {
        this.cdr.markForCheck();
      }
    }, 0);
  }

  isScheduleTask(): boolean {
    console.log('Selected Task Type ID:', this.selectedTaskTypeId);
    return this.selectedExecuteType?.taskType !== null && this.selectedExecuteType?.taskType === TASK_ID.Schedule;
  }

  private removeParameterControls(): void {
    if (this.selectedExecuteType) {
      this.selectedExecuteType?.parametrsJsonSchema?.forEach((field) => {
        if (field?.fieldName != null) {
          if (this.taskForm.contains(field.fieldName)) {
            this.taskForm.removeControl(field.fieldName);
          }
        }
      });
    }
  }

  shouldShowIntervalValue(): boolean {
    return this.taskForm.get('ScheduleType')?.value === ScheduleType.NUMBER_0;
  }

  shouldShowLaunchTime(): boolean {
    const scheduleType = this.taskForm.get('ScheduleType')?.value;
    return [
      ScheduleType.NUMBER_1,
      ScheduleType.NUMBER_2,
      ScheduleType.NUMBER_3,
      ScheduleType.NUMBER_4,
      ScheduleType.NUMBER_5,
    ].includes(scheduleType);
  }

  shouldShowDaysOfWeek(): boolean {
    return this.taskForm.get('ScheduleType')?.value === ScheduleType.NUMBER_2;
  }

  shouldShowDaysOfMonth(): boolean {
    return this.taskForm.get('ScheduleType')?.value === ScheduleType.NUMBER_3;
  }

  shouldShowMonths(): boolean {
    return this.taskForm.get('ScheduleType')?.value === ScheduleType.NUMBER_4;
  }

  getFieldLabel(field: any): string {
    if (field.FieldName === 'BackupPath') {
      return this.translate.instant('task.form.backup_path');
    }
    if (field.FieldName === 'CompressionLevel') {
      return this.translate.instant('task.form.compression_level');
    }

    return field.FieldLabels['en-US'] || field.FieldName;
  }

  getPossibleValues(field: any): { label: string; value: string }[] {
    if (!field.PosibleValues) return [];
    return Object.entries(field.PosibleValues).map(([key, value]) => ({
      label: value as string,
      value: key,
    }));
  }

  get translatedScheduleTypes() {
    return [
      { label: this.translate.instant('task.schedule.interval'), value: ScheduleType.NUMBER_0 },
      { label: this.translate.instant('task.schedule.daily'), value: ScheduleType.NUMBER_1 },
      { label: this.translate.instant('task.schedule.weekly'), value: ScheduleType.NUMBER_2 },
      { label: this.translate.instant('task.schedule.monthly'), value: ScheduleType.NUMBER_3 },
      { label: this.translate.instant('task.schedule.yearly'), value: ScheduleType.NUMBER_4 },
      { label: this.translate.instant('task.schedule.once'), value: ScheduleType.NUMBER_5 },
    ];
  }

  get translatedScheduleIntervals() {
    return [
      { label: this.translate.instant('task.interval.seconds'), value: ScheduleInterval.Seconds },
      { label: this.translate.instant('task.interval.minutes'), value: ScheduleInterval.Minutes },
      { label: this.translate.instant('task.interval.hours'), value: ScheduleInterval.Hours },
      { label: this.translate.instant('task.interval.days'), value: ScheduleInterval.Days },
    ];
  }

  get translatedDaysOfWeek() {
    return [
      { label: this.translate.instant('task.days.monday'), value: 1 },
      { label: this.translate.instant('task.days.tuesday'), value: 2 },
      { label: this.translate.instant('task.days.wednesday'), value: 3 },
      { label: this.translate.instant('task.days.thursday'), value: 4 },
      { label: this.translate.instant('task.days.friday'), value: 5 },
      { label: this.translate.instant('task.days.saturday'), value: 6 },
      { label: this.translate.instant('task.days.sunday'), value: 0 },
    ];
  }

  get translatedMonths() {
    return [
      { label: this.translate.instant('task.months.january'), value: 1 },
      { label: this.translate.instant('task.months.february'), value: 2 },
      { label: this.translate.instant('task.months.march'), value: 3 },
      { label: this.translate.instant('task.months.april'), value: 4 },
      { label: this.translate.instant('task.months.may'), value: 5 },
      { label: this.translate.instant('task.months.june'), value: 6 },
      { label: this.translate.instant('task.months.july'), value: 7 },
      { label: this.translate.instant('task.months.august'), value: 8 },
      { label: this.translate.instant('task.months.september'), value: 9 },
      { label: this.translate.instant('task.months.october'), value: 10 },
      { label: this.translate.instant('task.months.november'), value: 11 },
      { label: this.translate.instant('task.months.december'), value: 12 },
    ];
  }

  onScheduleTypeChange(value: ScheduleType): void {
    this.taskForm.get('ScheduleType')?.setValue(value);
  }

  onScheduleIntervalChange(value: ScheduleInterval): void {
    this.taskForm.get('ScheduleInterval')?.setValue(value);
  }

  onDaysOfWeekChange(value: number[]): void {
    this.taskForm.get('DaysOfWeek')?.setValue(value);
  }

  onDaysOfMonthChange(value: number[]): void {
    this.taskForm.get('DaysOfMonth')?.setValue(value);
  }

  onMonthsChange(value: number[]): void {
    this.taskForm.get('Months')?.setValue(value);
  }

  save(): void {
    if (this.taskForm.invalid) {
      this.markFormGroupTouched(this.taskForm);
      return;
    }

    console.log('Form Value:', this.taskForm.value);

    this.loading = true;
    const formValue = this.taskForm.value;

    const taskOptionJson: ScheduleTaskOptions = {};
    if (this.isScheduleTask()) {
      taskOptionJson.type = formValue.ScheduleType;
      taskOptionJson.interval = formValue.ScheduleInterval;
      taskOptionJson.frequency = formValue.IntervalValue;
      taskOptionJson.timeOfDay = formValue.LaunchTime;
      taskOptionJson.daysOfWeek = formValue.DaysOfWeek;
      taskOptionJson.daysOfMonth = formValue.DaysOfMonth;
      taskOptionJson.months = formValue.Months;
      taskOptionJson.timeZone = formValue.TimeZone;
      taskOptionJson.startDelaySeconds = formValue.StartDelaySeconds;
      taskOptionJson.groupName = this.selectedExecuteType?.groupName as string;
    }

    console.log('Task Option JSON to be saved:', taskOptionJson);

    const parametrsJson: any = {};
    if (this.selectedExecuteType) {
      this.selectedExecuteType?.parametrsJsonSchema?.forEach((field) => {
        if (field?.fieldName) {
          parametrsJson[field.fieldName] = formValue[field.fieldName];
        }
      });
    }

    console.log('Parametrs JSON to be saved:', parametrsJson);

    const backgroundTask: SystemBackgroundTask = {
      executeTypeName: formValue.ExecuteTypeName,
      isActive: formValue.IsActive,
      maxExecutions: formValue.MaxExecutions,
      isRetryable: formValue.IsRetryable,
      maxRetryCount: formValue.MaxRetryCount,
      taskOptionJson: JSON.stringify(taskOptionJson),
      parametrsJson: JSON.stringify(parametrsJson),
      names: this.selectedExecuteType?.names || {},
    };

    if (this.isEditMode) {
      this.systemBackgroundTaskService.commonSystemBackgroundTaskUpdatePut(backgroundTask).subscribe({
        next: (response) => {
          this.loading = false;
          this.ref.close({
            success: true,
            data: backgroundTask,
            response: response,
            isEdit: true,
          });
        },
        error: (err) => {
          console.error('Error when updating task:', err);
          this.loading = false;
        },
      });
    } else {
      this.systemBackgroundTaskService.commonSystemBackgroundTaskCreatePost(backgroundTask).subscribe({
        next: (response) => {
          this.loading = false;
          this.ref.close({
            success: true,
            data: backgroundTask,
            response: response,
            isEdit: false,
          });
        },
        error: (err) => {
          console.error('Error when creating task:', err);
          this.loading = false;
        },
      });
    }
  }

  cancel(): void {
    this.ref.close();
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  protected readonly TASK_ID = TASK_ID;

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
